import { Accordion, Button, Flex, Tooltip } from "@mantine/core";
import * as _ from "lodash-es";
import { DateTime } from "luxon";
import type { MRT_ColumnDef } from "mantine-react-table-open";
import {
  MantineReactTable,
  useMantineReactTable
} from "mantine-react-table-open";
import { useState } from "react";
import { getDefaultMRTOptions } from "../../../../../mantine/getDefaultMRTOptions";
import { getDefaultMRTTableEditModeOptions } from "../../../../../mantine/getDefaultMRTTableEditModeOptions";
import { ControlledMantineEditNumberInput } from "../../../../../mantine/mrt/components/edit/ControlledMantineEditNumberInput";
import { submitOrCancelOnKeyPress } from "../../../../../mantine/mrt/utils/submitOrCancelOnKeyPress";
import "./FixedPriceTable.scss";

export const PERIOD_FORMAT = "MMMM yyyy";
export const FIXED_PRICE_TABLE_ROW_LIMIT = 100;
const ALL_MONTHS_LABEL = "Alle Monate";

export interface DirektvermarktungContractFixedPriceTableData {
  period: string;
  revenue: number | undefined;
}

interface FixedPriceTableProps {
  data: Array<DirektvermarktungContractFixedPriceTableData>;
  onAllRevenuesChange: (newRevenue: number | undefined) => void;
  onRevenueChange: (
    newRevenue: number | undefined,
    startDate: DateTime,
    endDate: DateTime
  ) => void;
}

function FixedPriceTable({
  data,
  onRevenueChange,
  onAllRevenuesChange
}: FixedPriceTableProps) {
  const [allRevenuesValue, setAllRevenuesValue] = useState<number | undefined>(
    undefined
  );
  const dataWithEditAll =
    data.length === 0
      ? data
      : [
          {
            period: ALL_MONTHS_LABEL,
            revenue: allRevenuesValue
          },
          ...data
        ];

  function handleValueChange(value: number | undefined, period: string) {
    const periodDateTime = DateTime.fromFormat(period, PERIOD_FORMAT);
    const revenueStartDate = periodDateTime.startOf("month");
    const revenueEndDate = periodDateTime.endOf("month");
    onRevenueChange(value, revenueStartDate, revenueEndDate);
  }

  function handleApplySetAllRevenues() {
    if (typeof allRevenuesValue === "undefined") {
      return;
    }
    onAllRevenuesChange(allRevenuesValue);
  }

  const columns: Array<
    MRT_ColumnDef<DirektvermarktungContractFixedPriceTableData>
  > = [
    {
      accessorKey: "period",
      header: "Monat",
      enableEditing: false
    },
    {
      accessorKey: "revenue",
      header: "Tatsächlicher Erlös in €/MWh",
      Edit: ({ cell, column, row }) => {
        if (row.original.period === ALL_MONTHS_LABEL) {
          return (
            <Flex align={"center"} gap={"xs"} w={"100%"}>
              <ControlledMantineEditNumberInput
                alignRight
                cell={cell}
                columnId={column.id}
                label="Tatsächlicher Erlös in €/MWh"
                placeholder="-"
                row={row}
                style={{ flex: 1 }}
                onValueChange={(value) => {
                  const properNumberValue =
                    value.value === "" ? undefined : Number(value.floatValue);
                  setAllRevenuesValue(properNumberValue);
                }}
              />
              <Tooltip
                label="Alle Monate werden mit diesem Wert überschrieben"
                position="top"
              >
                <Button
                  disabled={!allRevenuesValue}
                  variant="default"
                  onClick={handleApplySetAllRevenues}
                >
                  Für alle Monate übernehmen
                </Button>
              </Tooltip>
            </Flex>
          );
        } else {
          return (
            <ControlledMantineEditNumberInput
              alignRight
              cell={cell}
              columnId={column.id}
              label="Tatsächlicher Erlös in €/MWh"
              overrideValue={cell.getValue<
                DirektvermarktungContractFixedPriceTableData["revenue"]
              >()}
              placeholder="-"
              row={row}
              onKeyDown={(event) => submitOrCancelOnKeyPress(event, table)}
              onValueChange={(value) => {
                const properNumberValue =
                  value.value === "" ? undefined : Number(value.floatValue);
                handleValueChange(properNumberValue, row.original.period);
              }}
            />
          );
        }
      }
    }
  ];

  const defaultOptions =
    getDefaultMRTOptions<DirektvermarktungContractFixedPriceTableData>({
      emptyRowsFallbackText: "Keine Monate im ausgewählten Zeitraum",
      hideBorder: true
    });

  const tableEditModeOptions =
    getDefaultMRTTableEditModeOptions<DirektvermarktungContractFixedPriceTableData>();

  const tableOptions = _.merge({}, defaultOptions, tableEditModeOptions, {
    enableSorting: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    mantineTableBodyRowProps: ({ row }) => {
      if (row.original.period === ALL_MONTHS_LABEL) {
        return {
          style: {
            backgroundColor:
              "color-mix(in srgb, var(--mantine-primary-color-filled), #FFF 90%)"
          }
        };
      }
      return {};
    }
  });

  const table = useMantineReactTable({
    ...tableOptions,
    columns,
    data: dataWithEditAll
  });

  return (
    <Accordion
      className="FixedPriceTable"
      defaultValue="fixed-price-table"
      mb="1rem"
    >
      <Accordion.Item value="fixed-price-table">
        <Accordion.Control>Tatsächliche Erlöse*</Accordion.Control>
        <Accordion.Panel>
          <MantineReactTable table={table} />
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}

export { FixedPriceTable, FixedPriceTableProps };
